/**
 * Biopet Essential — overlay positions for general info on PDF preview page 1.
 * Coordinates are px, relative to the page image container (top-left origin).
 * Tune x/y after comparing with your 1.png layout.
 *
 * displayFormat: 'd MMMM yyyy' — e.g. CSV/input "26/3/2026" → overlay "26 March 2026"
 */
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatDateInBangkokDMMMMyyyy(dateValue) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const parts = fmt.formatToParts(dateValue);
  const day = parts.find((p) => p.type === 'day')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const year = parts.find((p) => p.type === 'year')?.value;
  if (!day || !month || !year) return '';
  return `${parseInt(day, 10)} ${month} ${year}`;
}

function formatDMMMMyyyy(value) {
  const s = String(value).trim();
  if (!s) return s;

  // Datetime values from DB history (e.g. 2026-04-29T17:00:00.000Z)
  // should render as local calendar date in Thailand.
  if (/[T\s]\d{1,2}:\d{2}/.test(s) || /Z$/.test(s) || /[+\-]\d{2}:?\d{2}$/.test(s)) {
    const parsed = new Date(s);
    if (!isNaN(parsed.getTime())) {
      const formatted = formatDateInBangkokDMMMMyyyy(parsed);
      if (formatted) return formatted;
    }
  }

  const dmy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
  if (dmy) {
    const d = parseInt(dmy[1], 10);
    const m = parseInt(dmy[2], 10);
    const y = parseInt(dmy[3], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${d} ${MONTH_LONG[m - 1]} ${y}`;
    }
  }

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const m = parseInt(iso[2], 10);
    const d = parseInt(iso[3], 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${d} ${MONTH_LONG[m - 1]} ${y}`;
    }
  }

  return s;
}

/**
 * Pet age in years (from form/CSV) → life stage for preview columns / overlay.
 * Young: 0–1 y (inclusive), Adult: 2–7 y, Senior: >7 y (age 1 → Young).
 */
export function petAgeYearsToLifeStageLabel(raw) {
  if (raw === undefined || raw === null || String(raw).trim() === '') return '-';
  const n = parseFloat(String(raw).replace(',', '.').trim());
  if (!Number.isFinite(n) || n < 0) return '-';
  if (n <= 1) return 'Young';
  if (n <= 7) return 'Adult';
  return 'Senior';
}

/** Raw `health` cell from CSV row (form key `health`). */
export function petHealthRawFromRow(row) {
  if (!row) return '';
  const v = row.health ?? row.Health;
  if (v === undefined || v === null) return '';
  return String(v).trim();
}

/**
 * CSV `health` → preview label + colors. `normal` (case-insensitive) → Healthy (green); anything else non-empty → Unhealthy (red).
 */
export function petHealthCsvDisplay(raw) {
  const trimmed = raw === undefined || raw === null ? '' : String(raw).trim();
  if (!trimmed) {
    return { label: '-', variant: 'empty', raw: '' };
  }
  const isHealthy = trimmed.toLowerCase() === 'normal';
  return {
    label: isHealthy ? 'Healthy' : 'Unhealthy',
    variant: isHealthy ? 'healthy' : 'unhealthy',
    raw: trimmed
  };
}

/** Overlay text only; form/CSV values stay unchanged. */
export function formatBiopetOverlayDisplayValue(field, value) {
  if (value === undefined || value === null) return '';
  if (field?.displayFormat === 'd MMMM yyyy') {
    return formatDMMMMyyyy(value);
  }
  if (field?.displayFormat === 'capitalize-first') {
    const s = String(value).trim();
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }
  if (field?.displayFormat === 'pet-age-life-stage') {
    return petAgeYearsToLifeStageLabel(value);
  }
  return String(value);
}

/**
 * Page 1 renders every personal field in Noto Sans Thai so the block reads as one set —
 * the owner's name is usually Thai, and letting each field pick its own face by content
 * (Gilroy for Latin, Noto for Thai) made the values sit unevenly against each other.
 * Noto Sans Thai carries Latin glyphs too, so pet names, breeds and dates render from it
 * as well; the rest of the chain is fallback only.
 */
const PAGE1_FONT_FAMILY = "'Noto Sans Thai', 'Noto Sans', system-ui, sans-serif";

//
export const personalOverlayConfigBiopetEssential = {
  eng: {
    page: 1,
    fontFamily: "'Gilroy', system-ui, sans-serif",
    fields: [
      { key: "Owner's name", x: 58, y: 485, fontSize: 28, fontFamily: PAGE1_FONT_FAMILY },
      { key: 'Full Name', x: 205, y: 522, fontSize: 28, fontFamily: PAGE1_FONT_FAMILY },
      { key: 'species_animal', x: 180, y:558, fontSize: 28, displayFormat: 'capitalize-first', fontFamily: PAGE1_FONT_FAMILY },
      { key: 'breed', x: 155, y: 593, fontSize: 28, fontFamily: PAGE1_FONT_FAMILY },
      { key: 'collection_date', x: 284, y: 655, fontSize: 28, displayFormat: 'd MMMM yyyy', fontFamily: PAGE1_FONT_FAMILY },
      { key: 'report_date', x: 242, y: 690, fontSize: 28, displayFormat: 'd MMMM yyyy', fontFamily: PAGE1_FONT_FAMILY },
      { key: 'order_number', x: 266, y: 725, fontSize: 28, fontFamily: PAGE1_FONT_FAMILY },
      // Page-4 overlays (editable positions)
      // Page is 794px wide: keep these small enough that a long date (e.g. "18 September 2026")
      // starting at x=640 does not run off the right edge.
      { key: 'Full Name', page: 4, x: 155, y: 148, fontSize: 18 },
      { key: 'collection_date', page: 4, x: 640, y: 151, fontSize: 14, displayFormat: 'd MMMM yyyy' }
    ]
  }
};
